import React, { useMemo, useRef } from 'react';

import { Box, IIcon, Icon } from '@rbilabs/components-library';
import { isNil } from 'lodash-es';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

import { IStore, IStoreHoursOfOperation } from '@rbi-ctg/store';
import ActionButton from 'components/action-button';
import { Content, HeroContainer } from 'components/modal/css';
import { ImParticleEventData } from 'components/modal/types';
import Picture from 'components/picture';
import StaticMap from 'components/static-map';
import DirectionsLink from 'components/store-card/directions-link';
import { useGetRestaurantQuery } from 'generated/rbi-graphql';
import { useGetRestaurantsQuery } from 'generated/sanity-graphql';
import { useSelectRestaurant } from 'hooks/store-card';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { useRestaurantGroups } from 'hooks/use-restaurant-groups';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { LOYALTY_EXCLUDED_GROUP } from 'pages/store-locator/new-ui/store-card/constants';
import { useGeolocation } from 'state/geolocation';
import { useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import {
  getUSState,
  mergeRestaurantData,
  readableTimeInterval,
  useIsMobileOrderingAvailable,
} from 'utils/restaurant';
import { routes } from 'utils/routing';
import { getStoreSEOSlug } from 'utils/slugify';

import StoreMeta from './meta';
import {
  Address,
  AddressAndPhone,
  Amenity,
  Background,
  ButtonContainer,
  Container,
  FeatureIcon,
  Inner,
  LoyaltyTag,
  LoyaltyTagsContainer,
  PhoneNumber,
  SpacingLine,
  StoreHourContainer,
  StoreModal,
  StyledLoyaltyIcon,
} from './store-info-modal.styled';

interface IHoursProps {
  hours: IStoreHoursOfOperation;
  title: string;
}

const Hours = ({ title, hours }: IHoursProps) => {
  const { formatMessage } = useIntl();
  const hoursOfOperation = readableTimeInterval(
    formatMessage({ id: 'hoursOfOperationFallbackMessage' }),
    formatMessage({ id: 'open24Hours' }),
    formatMessage({ id: 'restaurantInfoTimeFormat' })
  );

  const storeHoursSummary: [string, string][] = [
    ['monday', 'mon'],
    ['tuesday', 'tue'],
    ['wednesday', 'wed'],
    ['thursday', 'thr'],
    ['friday', 'fri'],
    ['saturday', 'sat'],
    ['sunday', 'sun'],
  ];

  if (!hours) {
    return null;
  }

  return (
    <div>
      <h3>{title}</h3>
      {storeHoursSummary.map(([messageId, abbreviatedDay]) => {
        const openingHours = hours[`${abbreviatedDay}Open`];
        const closingHours = hours[`${abbreviatedDay}Close`];
        return (
          <StoreHourContainer key={messageId}>
            <div>{formatMessage({ id: messageId })}</div>
            <div>{hoursOfOperation(openingHours, closingHours)}</div>
          </StoreHourContainer>
        );
      })}
    </div>
  );
};

interface IStoreInfoModalProps {
  disableCanonical?: boolean;
  onDismiss?: () => void;
  onOpen?: () => void;
  mParticleEventData: ImParticleEventData;
  showOrderCTA?: boolean;
  store: IStore & { available: boolean };
}

export const StoreInfoModal = ({
  disableCanonical = false,
  mParticleEventData,
  onDismiss,
  onOpen,
  showOrderCTA = true,
  store,
}: IStoreInfoModalProps) => {
  const {
    name: storeName,
    number,
    hasLoyalty,
    phoneNumber,
    restaurantImage,
    latitude: lat,
    longitude: lng,
    physicalAddress,
  } = store;
  const { current: position } = useRef({ lat, lng });
  const { activeCoordinates } = useGeolocation();
  const { pathname }: { pathname: string } = useLocation();
  const { formatMessage } = useIntl();
  const { fetchingPosData } = useOrderContext();
  const { storeHasMobileLoyalty } = useRestaurantGroups(LOYALTY_EXCLUDED_GROUP);
  const {
    restaurantCanBeSelected,
    serviceModeStatus,
    allServiceModesUnavailable,
  } = useServiceModeStatus(store);
  const enableOrderingFlag = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const enableAmenitiesSection = useFlag(LaunchDarklyFlag.ENABLE_AMENITIES_SECTION);
  const enableHideOrderHereButton = useFlag(
    LaunchDarklyFlag.ENABLE_HIDE_STORE_MODAL_ORDER_HERE_BUTTON
  );
  const enableShowAddress2BeforeCity = useFlag(LaunchDarklyFlag.ENABLE_SHOW_ADDRESS2_BEFORE_CITY);
  const enableLoyaltyTags = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_TAGS_STORE_LOCATOR);
  const enableOrdering = isNil(enableOrderingFlag) || enableOrderingFlag;

  const { selectRestaurant } = useSelectRestaurant({ restaurant: store });

  useEffectOnce(() => onOpen?.());

  const streetAddress = physicalAddress?.address1;
  const address2 = physicalAddress?.address2;
  const formattedCity = physicalAddress?.city;
  const state = getUSState(physicalAddress?.stateProvince);
  const postalCode = physicalAddress?.postalCode?.split('-')[0];
  const shouldShowWaysToOrderSection =
    useIsMobileOrderingAvailable(store) || !allServiceModesUnavailable;

  const canonicalUrl = [`${routes.store}`, getStoreSEOSlug(store)].join('/');
  const isStoreLocatorPage = pathname.includes(routes.store);

  const shouldShowDineIn = serviceModeStatus.EAT_IN.capable && !serviceModeStatus.EAT_IN.disabled;
  const shouldShowTakeout =
    serviceModeStatus.TAKEOUT.capable && !serviceModeStatus.TAKEOUT.disabled;
  const shouldShowDriveThru =
    serviceModeStatus.DRIVE_THRU.capable && !serviceModeStatus.DRIVE_THRU.disabled;
  const shouldShowCurbside =
    serviceModeStatus.CURBSIDE.capable && !serviceModeStatus.CURBSIDE.disabled;

  const showAddress2 = enableShowAddress2BeforeCity && address2;

  const hasCatering =
    serviceModeStatus.CATERING_DELIVERY.available || serviceModeStatus.CATERING_PICKUP.available;

  const amenitiesBreakdown: [keyof IIcon, boolean, string, string][] = [
    ['driveThru', serviceModeStatus.DRIVE_THRU.available, '16px', 'driveThru'],
    ['mobile', useIsMobileOrderingAvailable(store), '22px', 'mobileOrdering'],
    ['delivery', serviceModeStatus.DELIVERY.available, '22px', 'delivery'],
    ['curbside', serviceModeStatus.CURBSIDE.available, '22px', 'curbside'],
    ['catering', hasCatering, '20px', 'catering'],
  ];

  const storeHoursBreakdown = [
    [shouldShowDineIn || shouldShowTakeout, 'dineInHours', 'diningRoomHours'],
    [shouldShowDriveThru, 'driveThruHours'],
    [shouldShowCurbside, 'curbsideHours'],
  ];

  const orderButtonEnabled = restaurantCanBeSelected && !fetchingPosData;

  const amenitiesSection = useMemo(() => {
    if (!store.amenities?.length || !enableAmenitiesSection) {
      return null;
    }

    const amenitiesWithIconSrc = store.amenities.filter(amenity => amenity?.icon?.asset?.url);

    if (!amenitiesWithIconSrc.length) {
      return null;
    }

    return (
      <section data-testid="restaurant-amenities">
        <h3 data-testid="in-store-amenities">{formatMessage({ id: 'inStoreAmenities' })}</h3>
        {amenitiesWithIconSrc.map(amenity => {
          const name = amenity?.name?.locale ?? '';

          return (
            <Amenity key={name}>
              <FeatureIcon src={amenity?.icon?.asset?.url} alt={name} />
              {name}
            </Amenity>
          );
        })}
      </section>
    );
  }, [enableAmenitiesSection, formatMessage, store]);

  return (
    <>
      {!disableCanonical && (
        <Helmet data-testid="canonical" link={[{ rel: 'canonical', href: canonicalUrl }]} />
      )}
      <StoreModal
        onDismiss={onDismiss}
        data-testid="store-info-modal"
        mParticleEventData={mParticleEventData}
      >
        {isStoreLocatorPage && (
          <StoreMeta
            storeUrl={window.location.href}
            atCopy={formatMessage({ id: 'at' })}
            streetAddress={streetAddress}
            city={formattedCity}
            state={state}
            postalCode={postalCode}
            phoneNumber={phoneNumber}
            image={restaurantImage}
          />
        )}
        <Container>
          <HeroContainer>
            {restaurantImage ? (
              <Picture image={restaurantImage} alt={storeName} />
            ) : (
              <StaticMap position={position} />
            )}
          </HeroContainer>
          <Content disableFooter={!enableOrdering}>
            <Background>
              <Inner>
                <h2 id="modal-heading">{streetAddress.toLowerCase()}</h2>
                <AddressAndPhone>
                  <DirectionsLink activeCoordinates={activeCoordinates} position={position} />
                  <Address>
                    {showAddress2 && <span data-testid="address2">{address2}, </span>}
                    {`${formattedCity}${state ? ` ${state}` : ''}, ${postalCode}`}
                  </Address>
                  {phoneNumber && (
                    <PhoneNumber href={`tel:${phoneNumber}`}>{phoneNumber}</PhoneNumber>
                  )}
                </AddressAndPhone>
                {enableLoyaltyTags && (
                  <LoyaltyTagsContainer>
                    {hasLoyalty ? (
                      <LoyaltyTag>
                        <StyledLoyaltyIcon />
                        {formatMessage({ id: 'loyaltyStoreLocatorInStore' })}
                      </LoyaltyTag>
                    ) : null}
                    {storeHasMobileLoyalty(number) ? (
                      <LoyaltyTag>
                        <StyledLoyaltyIcon />
                        {formatMessage({ id: 'loyaltyStoreLocatorMobile' })}
                      </LoyaltyTag>
                    ) : null}
                  </LoyaltyTagsContainer>
                )}
                <SpacingLine top={24} bottom={11} />
                {enableOrdering && (
                  <>
                    {shouldShowWaysToOrderSection && (
                      <h3>{formatMessage({ id: 'waysToOrder' })}</h3>
                    )}
                    {amenitiesBreakdown.map(
                      ([icon, shouldShow, height, messageId]) =>
                        shouldShow && (
                          <Amenity key={messageId}>
                            <Icon
                              icon={icon}
                              color="icon-default"
                              width="40px"
                              height={height}
                              aria-hidden
                            />
                            {formatMessage({ id: messageId })}
                          </Amenity>
                        )
                    )}
                    {shouldShowWaysToOrderSection && <SpacingLine top={4} bottom={15} />}
                  </>
                )}

                {amenitiesSection}
                {storeHoursBreakdown.map(
                  ([shouldShow, messageId, hours = messageId]: [boolean, string, string]) =>
                    shouldShow && (
                      <Hours
                        key={messageId}
                        title={formatMessage({ id: messageId })}
                        hours={store[hours]}
                      />
                    )
                )}
              </Inner>
            </Background>
          </Content>
          {showOrderCTA && enableOrdering && (
            <ButtonContainer>
              <Box maxWidth="300px" margin="0 auto">
                {!enableHideOrderHereButton && (
                  <ActionButton
                    isLoading={fetchingPosData}
                    data-testid="store-modal-order-here"
                    fullWidth
                    disabled={!orderButtonEnabled}
                    onClick={() => selectRestaurant()}
                  >
                    {formatMessage({ id: 'orderHere' })}
                  </ActionButton>
                )}
              </Box>
            </ButtonContainer>
          )}
        </Container>
      </StoreModal>
    </>
  );
};

interface IStoreInfoModalContainerProps {
  storeId?: string;
  disableCanonical?: boolean;
  canonicalUrl?: string;
  storeNumber?: string;
  onDismiss: () => void;
  onOpen?: () => void;
  showOrderCTA?: boolean;
}

const StoreInfoModalContainer = (props: IStoreInfoModalContainerProps) => {
  const { id: storeIdFromUrl, storeNumber: storeNumberFromUrl } = useParams();

  const id = props.storeId || storeIdFromUrl;
  const storeNumber = props.storeNumber || storeNumberFromUrl;

  const [ErrorDialog] = useErrorModal({
    init: true,
    modalAppearanceEventMessage: 'Error: Fetching Restaurant Info Failure',
  });
  const getRestaurantFromSanity = useGetRestaurantsQuery({
    skip: !storeNumber && !id,
    variables: {
      filter: {
        _id: id,
        number: storeNumber,
      },
      limit: 1,
    },
  });
  const secondQueryVariables = useMemo(
    () => ({
      storeId: storeNumber || getRestaurantFromSanity?.data?.allRestaurants[0].number,
    }),
    [storeNumber, getRestaurantFromSanity]
  );
  const getRestaurantFromRBI = useGetRestaurantQuery({
    fetchPolicy: 'cache-and-network',
    skip: !secondQueryVariables.storeId,
    variables: secondQueryVariables,
  });

  const { loading, restaurant, error } = useMemo(
    () => ({
      loading: getRestaurantFromRBI.loading || getRestaurantFromSanity.loading,
      restaurant:
        getRestaurantFromRBI.data &&
        getRestaurantFromSanity.data &&
        getRestaurantFromSanity.data.allRestaurants.length !== 0
          ? mergeRestaurantData({
              rbiRestaurant: getRestaurantFromRBI.data.restaurant,
              sanityStore: getRestaurantFromSanity.data.allRestaurants[0] as any,
            })
          : undefined,
      error: getRestaurantFromRBI.error || getRestaurantFromSanity.error || undefined,
    }),
    [getRestaurantFromRBI, getRestaurantFromSanity]
  );

  // Should show loading state
  if (loading) {
    return null;
  }

  if (error || !restaurant) {
    return <ErrorDialog />;
  }

  return (
    <StoreInfoModal
      {...props}
      mParticleEventData={{ modalAppearanceEventMessage: 'Store Details' }}
      store={restaurant}
    />
  );
};

export default StoreInfoModalContainer;
